'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "duration", "reverse", "Wrapper"];
import { PANEL_NAVIGATOR_TRANSITION_TIMING } from 'HubStyleTokens/times';
import PropTypes from 'prop-types';
import { useState } from 'react';
import useMounted from 'react-utils/hooks/useMounted';
import styled, { css } from 'styled-components';
import { percentToVW, toPx } from '../../utils/Styles';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const SlideCrossTransition = _ref => {
  let {
      children,
      duration,
      reverse,
      Wrapper
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [outgoingChild, setOutgoingChild] = useState(null);
  const [prevChild, setPrevChild] = useState(children);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);
  const mountedRef = useMounted();
  if (prevChild.key !== children.key) {
    setTransitioning(true);
    setOutgoingChild(prevChild);
    setPrevChild(children);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        setTransitionActive(true);
        setTimeout(() => {
          if (!mountedRef.current) return;
          setOutgoingChild(null);
          setTransitioning(false);
          setTransitionActive(false);
        }, duration);
      });
    });
  }
  return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
    "data-transition-complete": !transitioning,
    distance: outgoingChild ? percentToVW(toPx(outgoingChild.props.width)) : null,
    duration: duration,
    reverse: reverse,
    style: {
      width: percentToVW(children.props.width)
    },
    transitioning: transitioning,
    transitionActive: transitionActive,
    children: [outgoingChild, children]
  }));
};

// For the first frame where the incoming content is rendered, we want that content to be on-screen
// to prevent glitches if it contains any controls with autofocus (#8454). The trick here is to hide
// the incoming content under the old content. It's on-screen as far as the browser is concerned!
const onscreenRenderMixin = css(["& >:last-child{position:absolute !important;right:0 !important;z-index:-1;}"]);
const PanelNavigatorTransitionWrapper = styled.div.withConfig({
  displayName: "PanelNavigatorTransition__PanelNavigatorTransitionWrapper",
  componentId: "knzfgf-0"
})(["position:relative;display:flex;flex-direction:", ";height:100%;transform:translateX( ", " );transition-property:", ";transition-duration:", "ms;transition-timing-function:cubic-bezier(0.16,0.84,0.44,1);", ";"], ({
  reverse
}) => reverse ? 'row-reverse' : 'row', ({
  distance,
  reverse,
  transitioning,
  transitionActive
}) => {
  if (transitioning) {
    if (reverse) {
      return transitionActive ? `${distance}` : '0';
    }
    return transitionActive ? `-${distance}` : '0';
  }
  return '0';
}, ({
  transitionActive
}) => transitionActive ? 'transform, width' : 'width', ({
  duration
}) => duration, ({
  transitioning,
  transitionActive
}) => transitioning && !transitionActive && onscreenRenderMixin);
export default function PanelNavigatorTransition(props) {
  return /*#__PURE__*/_jsx(SlideCrossTransition, Object.assign({}, props));
}
PanelNavigatorTransition.propTypes = {
  align: PropTypes.oneOf(['left', 'right']).isRequired,
  duration: PropTypes.number.isRequired,
  Wrapper: PropTypes.elementType.isRequired
};

/* eslint-disable-next-line hubspot-dev/no-default-props-assignment */
PanelNavigatorTransition.defaultProps = {
  align: 'right',
  duration: parseInt(PANEL_NAVIGATOR_TRANSITION_TIMING, 10),
  Wrapper: PanelNavigatorTransitionWrapper
};
PanelNavigatorTransition.displayName = 'PanelNavigatorTransition';